// Utility functions for authentication
import { firebaseAuthService } from '../services/firebaseAuthService';

export const getAuthToken = async (forceRefresh: boolean = false): Promise<string | null> => {
  // Check for JWT token first (for traditional login)
  const jwtToken = localStorage.getItem('token');
  if (jwtToken) {
    // If force refresh is requested, we can't refresh JWT tokens, so return as-is
    return jwtToken;
  }
  
  // Try to get fresh Firebase token (force refresh if requested)
  const firebaseToken = await firebaseAuthService.getFirebaseToken(forceRefresh);
  if (firebaseToken) {
    return firebaseToken;
  }
  
  // If force refresh was requested but we got null, don't fall back to stored token
  // as it's likely expired
  if (forceRefresh) {
    console.warn('⚠️ Force refresh requested but no token obtained');
    return null;
  }
  
  // Fallback to stored Firebase token (only if not forcing refresh)
  const storedToken = localStorage.getItem('firebaseToken');
  if (storedToken) {
    console.log('⚠️ Using stored Firebase token (may be expired)');
  }
  return storedToken;
};

export const getAuthTokenSync = (): string | null => {
  // Synchronous version for backward compatibility
  // Try JWT token first (for traditional login), then Firebase token
  return localStorage.getItem('token') || localStorage.getItem('firebaseToken');
};

export const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthTokenSync();
  console.log('🔍 getAuthHeaders - token:', token ? 'Present' : 'Missing');
  if (!token) {
    console.log('❌ No token found in localStorage');
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`
  };
};

// Async version that refreshes Firebase token if needed
export const getAuthHeadersAsync = async (forceRefresh: boolean = false): Promise<Record<string, string>> => {
  const token = await getAuthToken(forceRefresh);
  console.log('🔍 getAuthHeadersAsync - token:', token ? 'Present' : 'Missing');
  if (!token) {
    console.log('❌ No token found');
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`
  };
};

export const clearAuthTokens = (): void => {
  localStorage.removeItem('firebaseToken');
  localStorage.removeItem('token');
};

export const isAuthenticated = (): boolean => {
  return !!getAuthTokenSync();
};
